import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useState } from "react";
import {
  type ActionFunction,
  type LoaderFunction,
  Navigate,
  redirect,
  useLoaderData,
  useNavigate,
  useNavigation,
  useSubmit,
} from "react-router-dom";

import {
  getPlatformUserMemberships,
  postPlatformUserMembershipsByPlatformUserMembershipIdFreezeRequests,
} from "api/methods";
import { invalidateMembershipFreezeQueries } from "./membershipHelper";
import { Button } from "atoms/button/Button";
import Dropdown from "atoms/dropdown/Dropdown";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import { TextArea } from "atoms/TextArea/TextArea";
import { useTranslation } from "@/core/i18n";
import { usePlatform } from "hooks/usePlatform";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { useFeatureFlag } from "services/Flags";
import { getBillingIntervalBase, parseBillingPeriodName } from "utils/billingPeriod.helpers";
import pluralize from "utils/pluralize";
import { readFormData } from "toolbox/ReadFormData";

const load = async (platformUserMembershipId: string) => {
  const userMemberships = await queryClient.fetchQuery({ ...getPlatformUserMemberships.keyed() });

  const userMembership = userMemberships.find(
    (m) => m.platformUserMembershipId === platformUserMembershipId,
  );

  if (
    !userMembership ||
    !userMembership.membership.allowFreeze ||
    userMembership.membership.maxFreezeBillingPeriods < 1 ||
    userMembership.activeMembershipFreezeId
  ) {
    return redirect("/wallet/memberships/manage");
  }

  return {
    platformUserMembershipId,
    userMemberships,
  };
};

type LoaderData = Exclude<Awaited<ReturnType<typeof load>>, Response>;
export const walletMembershipFreezeLoader: LoaderFunction = ({
  params: { platformUserMembershipId },
}) => {
  if (!platformUserMembershipId) {
    return redirect("..");
  }

  return load(platformUserMembershipId);
};

export const walletMembershipFreezeAction: ActionFunction = async ({ request }) => {
  const { platformUserMembershipId, billingPeriods, requestReason } = await readFormData<{
    platformUserMembershipId: string;
    billingPeriods: string;
    requestReason: string;
  }>(request);

  const trimmedReason = requestReason?.trim();

  await postPlatformUserMembershipsByPlatformUserMembershipIdFreezeRequests({
    platformUserMembershipId,
    billingPeriods: Number(billingPeriods),
    requestReason: trimmedReason ? trimmedReason : undefined,
  });

  await invalidateMembershipFreezeQueries(queryClient);

  return redirect("/wallet/memberships/manage");
};

export const WalletMembershipFreezePage = () => {
  const { platformUserMembershipId, userMemberships: initialUserMemberships } =
    useLoaderData() as LoaderData;

  const navigate = useNavigate();
  const submit = useSubmit();
  const navigation = useNavigation();
  const isFlexibleMembershipsEnabled = useFeatureFlag("flexible_membership_terms");
  const { platform } = usePlatform();
  const { t } = useTranslation();

  const { data: userMemberships } = useQuery({
    ...getPlatformUserMemberships.keyed(),
    initialData: initialUserMemberships,
  });

  const [selectedBillingPeriods, setSelectedBillingPeriods] = useState("1");
  const [requestReason, setRequestReason] = useState("");

  const userMembership = userMemberships.find(
    (m) => m.platformUserMembershipId === platformUserMembershipId,
  );

  if (!userMembership) {
    return <Navigate replace to="/wallet/memberships/manage" />;
  }

  const { name, billingPeriodInterval, billingPeriodIntervalCount, maxFreezeBillingPeriods } =
    userMembership.membership;

  const baseBillingPeriodInterval = parseBillingPeriodName(billingPeriodInterval);
  const billingPeriodIntervalName = isFlexibleMembershipsEnabled
    ? (getBillingIntervalBase(baseBillingPeriodInterval) as dayjs.ManipulateType)
    : "month";
  const billingPeriodLength = isFlexibleMembershipsEnabled ? billingPeriodIntervalCount : 1;

  const formatFreezeLength = (billingPeriods: number) => {
    const total = billingPeriods * billingPeriodLength;
    return `${total} ${pluralize(total, billingPeriodIntervalName, `${billingPeriodIntervalName}s`)}`;
  };

  const freezeLengthOptions = Array.from({ length: maxFreezeBillingPeriods }, (_, index) => ({
    id: `${index + 1}`,
    name: formatFreezeLength(index + 1),
  }));

  const startsAt = dayjs(userMembership.expiration);
  const resumesAt = startsAt.add(
    Number(selectedBillingPeriods) * billingPeriodLength,
    billingPeriodIntervalName,
  );

  const platformName = platform?.name || "your provider";
  const platformNameAtSentenceStart = platform?.name || "Your provider";

  return (
    <div className="relative flex h-full flex-col bg-white">
      <TopNav
        startIconSVG={ChevronLeft}
        showHeaderItems={false}
        onStartIconClick={() => navigate(-1)}
      >
        <div className="flex justify-center text-sub2">REQUEST A FREEZE</div>
      </TopNav>
      <div className="flex flex-col gap-y-6 bg-white px-7 pb-5 pt-3 text-left">
        <div className="flex flex-col gap-y-1">
          <h1 className="text-title">Request a freeze</h1>
          <div className="text-body2 text-secondary">for your {name} membership</div>
        </div>
        <div className="flex flex-col rounded-lg bg-white shadow-[0px_2px_10px_rgba(0,0,0,0.06)]">
          <div className="flex items-center justify-between border-b border-light-grey p-4">
            <span className="text-body2 text-secondary">Freeze starts</span>
            <span className="text-bold1">{startsAt.format("MMM D, YYYY")}</span>
          </div>
          <div className="flex items-center justify-between p-4">
            <span className="text-body2 text-secondary">Membership resumes</span>
            <span className="text-bold1">{resumesAt.format("MMM D, YYYY")}</span>
          </div>
        </div>
        <Dropdown
          options={freezeLengthOptions}
          value={selectedBillingPeriods}
          setValue={setSelectedBillingPeriods}
          textValue={formatFreezeLength(Number(selectedBillingPeriods))}
          label="Freeze length"
        />
        <TextArea
          label={t("walletMembershipFreezePage.requestReasonLabel")}
          placeholder={t("walletMembershipFreezePage.requestReasonPlaceholder", { platformName })}
          value={requestReason}
          onChange={setRequestReason}
          maxLength={500}
          rows={3}
          visible
        />
        <div className="flex flex-col gap-y-4 text-body2 text-secondary">
          <div>
            Ask {platformName} to pause your {name} membership for a little while. If they approve,
            your freeze starts on{" "}
            <span className="text-bold1 text-primary">{startsAt.format("MMM D, YYYY")}</span> — the
            end of your current billing period — and your membership resumes automatically on{" "}
            <span className="text-bold1 text-primary">{resumesAt.format("MMM D, YYYY")}</span>.
          </div>
          <div>You won&apos;t be billed while your membership is frozen.</div>
          <div>
            {platformNameAtSentenceStart} will review your request. You can withdraw it any time
            before they decide.
          </div>
        </div>
        <div className="flex flex-col gap-y-3">
          <Button
            fullWidth
            style="primary"
            disabled={navigation.state !== "idle"}
            onClick={() => {
              submit(
                {
                  platformUserMembershipId,
                  billingPeriods: selectedBillingPeriods,
                  requestReason,
                },
                { method: "post" },
              );
            }}
          >
            Request freeze
          </Button>
          <Button
            fullWidth
            style="secondary"
            onClick={() => navigate("/wallet/memberships/manage")}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};
